import { count } from '../../i18n/format';
import { niceCeiling } from './buckets';

export interface ColumnSegment {
  readonly value: number;
  readonly colour: string;
}

export interface Column {
  readonly key: string;
  /** Under the column; printed for every few columns only, so labels never collide. */
  readonly label: string;
  /** The hover and screen-reader text: the whole bucket, worded. */
  readonly description: string;
  /** Bottom first. */
  readonly segments: readonly ColumnSegment[];
}

/** About how many axis labels fit under the chart on a phone before they touch. */
const MAX_LABELS = 6;

/**
 * Change over time as vertical columns, stacked when there is more than one segment (CR-33).
 *
 * Columns rather than a line because each column is a count of things in a bucket — appointments
 * that day, patients that month — and a line drawn between two days implies there was something
 * in between. Built from flex boxes like the bars, for the same reason: it mirrors with the rest of
 * the interface for Arabic, where time reads from the right.
 *
 * Two gridlines (half and the top) carry the scale, in the recessive grid colour, labelled at the
 * start edge; every column shows its full breakdown on hover and focus. `current` marks one column
 * (today, on the home screen, CR-34): it is shaded and always labelled, and the other labels are
 * spaced from it so none collide.
 */
export function Columns({
  columns,
  label,
  formatTick = count,
  current,
}: {
  readonly columns: readonly Column[];
  /** What the whole chart shows, for screen readers. */
  readonly label: string;
  /** Writes the scale's figures: money is shortened (12K), a rate takes its %. */
  readonly formatTick?: (value: number) => string;
  /** The key of the column to mark. */
  readonly current?: string;
}) {
  const totals = columns.map((column) => column.segments.reduce((sum, segment) => sum + segment.value, 0));
  const largest = niceCeiling(totals.reduce((running, value) => Math.max(running, value), 0));
  const every = Math.max(1, Math.ceil(columns.length / MAX_LABELS));
  const anchor = Math.max(0, columns.findIndex((column) => column.key === current));
  const labelled = (index: number) => (((index - anchor) % every) + every) % every === 0;
  // Every second label is minor and gives way on a phone, where "26 سبتمبر" six times does not
  // fit; the current column's label is never minor.
  const minor = (index: number) => labelled(index) && Math.abs(Math.round((index - anchor) / every)) % 2 === 1;

  return (
    <figure className="columns" aria-label={label}>
      <div className="columns__plot">
        <div className="columns__grid" aria-hidden="true">
          <span className="columns__gridline" style={{ insetBlockEnd: '100%' }}>
            <span className="columns__tick">{formatTick(largest)}</span>
          </span>
          <span className="columns__gridline" style={{ insetBlockEnd: '50%' }}>
            <span className="columns__tick">{formatTick(largest / 2)}</span>
          </span>
          <span className="columns__gridline columns__gridline--base" style={{ insetBlockEnd: 0 }}>
            <span className="columns__tick">0</span>
          </span>
        </div>
        <ol className="columns__bars">
          {columns.map((column, index) => (
            <li
              key={column.key}
              className={column.key === current ? 'columns__slot columns__slot--current has-tip' : 'columns__slot has-tip'}
              tabIndex={0}
              aria-label={column.description}
            >
              <span
                className="columns__bar"
                style={{ blockSize: `${largest > 0 ? ((totals[index] ?? 0) / largest) * 100 : 0}%` }}
              >
                {column.segments.map((segment, segmentIndex) =>
                  segment.value > 0 ? (
                    <span key={segmentIndex} style={{ flexGrow: segment.value, background: segment.colour }} />
                  ) : null,
                )}
              </span>
              <span className="chart-tip" role="presentation">
                {column.description}
              </span>
            </li>
          ))}
        </ol>
      </div>
      <ol className="columns__labels" aria-hidden="true">
        {columns.map((column, index) => (
          <li
            key={column.key}
            className={
              column.key === current ? 'columns__label--current' : minor(index) ? 'columns__label--minor' : undefined
            }
          >
            {labelled(index) ? column.label : ''}
          </li>
        ))}
      </ol>
    </figure>
  );
}

